<template>
  <div class="container-fluid">
    <div class="row page-titles">
      <div class="col-md-5 col-8 align-self-center">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/home">Home</router-link>
          </li>
          <li class="breadcrumb-item active">シフト表</li>
        </ol>
      </div>
    </div>
    <div class="row">
      <div class="col-md-3 pr-10">
        <b-card class="mt-10">
          <b-card-body>
            <div>
              <p class="mb-0">事業所</p>
              <select-office v-model="inputs.selectedOfficeId" :disabled="isProcessing"></select-office>
            </div>
            <div class="pt-2" v-if="false">
              <p class="mb-0">利用者表示</p>
              <div class="switch pt-1">
                <label>
                  全利用者
                  <input type="checkbox" v-model="inputs.userViewMode" @change.prevent="onChangeUserViewMode"><span
                  class="lever"></span>
                  担当利用者のみ
                </label>
              </div>
              <div>
                <p class="text-right">
                  <router-link :to="`/staff/${profile.id}`">担当利用者を設定する</router-link>
                </p>
              </div>
            </div>
            <div class="pt-2">
              <p class="mb-0">スタッフ一覧</p>
              <staff-search @change="addSelectedStaff" :staffs="staffs" :clear-after-selected="true"
                            :disabled="isProcessing || !(inputs.selectedOfficeId)"/>
              <staff-check-box-list v-model="inputs.selectedStaffs"
                                    :disabled="isProcessing || !(inputs.selectedOfficeId)"/>
            </div>
          </b-card-body>
        </b-card>
      </div>
      <div class="col-md-9">
        <div class="card">
          <div class="card-body staff-shift" :class="{'staff-shift-week': isWeekMode, 'staff-shift-day': !isWeekMode}"
               @mousemove="mousemove($event)">
            <div class="tooltipevent" v-if="tip.active" :style="tipPlace">
              時間：{{ tip.event.extendedProps.caretime }}<br>
              サービス：{{ tip.event.extendedProps.service }}<br>
            </div>
            <full-calendar
              scheduler-license-key="0535771462-fcs-1587969786"
              ref="calendar"
              :dates-above-resources="true"
              :all-day-slot="false"
              :editable="!isProcessing"
              :droppable="!isProcessing"
              :event-limit="true"
              slot-width="1000"
              min-time="00:00:00"
              max-time="24:00:00"
              scroll-time="08:00:00"
              :default-view="calendar.defaultView"
              :plugins="calendar.calendarPlugins"
              :resources="calendar.resources"
              resource-order="sort"
              :locale="calendar.locale"
              :views="calendar.views"
              :header="calendar.header"
              :events="calendar.events"
              :custom-buttons="calendar.customButtons"
              :default-date="calendar.defaultDate"
              @dateClick="onDateClick"
              @eventClick="onEventClickOuter"
              @eventMouseEnter="onMouseover"
              @eventMouseLeave="onMouseout"
              @eventResize="onEventResize"
              @eventDrop="onEventDropOuter"
              @datesRender="datesRender"
            />
          </div>
        </div>
      </div>
    </div>
    <copy-day-modal name="copy-day-modal" @copy="copyDay"/>
    <copy-week-modal name="copy-week-modal" @copy="copyWeek"/>
    <plan-edit-modal name="plan-edit-modal"
                     :office-id="inputs.selectedOfficeId"
                     :staffs="staffs"
                     :users="users"
                     :editable="true"
                     @close="onPlanEditModalClosed"/>
    <v-dialog :click-to-close="false"/>
  </div>
</template>

<script>
import _ from 'lodash'
import co from 'co'
import { mapActions, mapGetters, mapState } from 'vuex'
import dayjs from 'dayjs'
import FullCalendar from '@fullcalendar/vue'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import locale from '@fullcalendar/core/locales/ja'
import StaffCheckBoxList from '../../components/StaffCheckBoxList'
import SelectOffice from '../../components/SelectOffice'
import constants from '../../constants'
import utils from '../../libs/Utils'
import api from '../../libs/Api'
import StaffSearch from '../../components/StaffSearch'
import PlanEditModal from '../../components/PlanEditModal'
import CopyWeekModal from '../../components/shift/CopyWeekModal'
import CopyDayModal from '../../components/shift/CopyDayModal'

const DATE_RANGE = 7

function convertPlan2Event (plan) {
  return _.map(plan.staff, staff => {
    const mark = plan.staff.length > 1 ? '*' : ''
    return {
      id: plan.id,
      title: `${mark}${plan.user.display_name}`,
      groupId: plan.id,
      resourceId: staff.id,
      start: utils.formatDay(plan.service.start_day) + 'T' + utils.formatHHMM(plan.service.start_hhmm) + ':00',
      end: utils.formatDay(plan.service.end_day) + 'T' + utils.formatHHMM(plan.service.end_hhmm) + ':00',
      tag: plan,
      service: (plan.service.service_code_id) ? plan.service.service_code.display_name : plan.service.service_type.display_name,
      caretime: utils.formatHHMM(plan.service.start_hhmm) + ' ～ ' + utils.formatHHMM(plan.service.end_hhmm),
      color: utils.getEventColor(plan)
    }
  })
}

function convertEvent2Plan (event) {
  const plan = event.extendedProps.tag
  return {
    id: plan.id,
    user_id: plan.user_id,
    office_id: plan.office_id,
    staff: _.map(plan.staff, staff => {
      return { staff_id: staff.id }
    }),
    service: {
      service_type_id: plan.service.service_type_id,
      start_day: dayjs(event.start).format('YYYYMMDD'),
      start_hhmm: dayjs(event.start).format('HHmm'),
      end_day: dayjs(event.end).format('YYYYMMDD'),
      end_hhmm: dayjs(event.end).format('HHmm')
    }
  }
}

export default {
  name: 'StaffShift',
  components: {
    CopyDayModal,
    CopyWeekModal,
    PlanEditModal,
    StaffSearch,
    SelectOffice,
    StaffCheckBoxList,
    FullCalendar
  },
  watch: {
    'inputs.selectedOfficeId': function (selectedOfficeId) {
      this.exec(_this => {
        _this.isProcessing = true
        return _this.selectOffice({ officeId: selectedOfficeId, userViewMode: _this.inputs.userViewMode })
          .then(() => {
            _this.inputs.selectedOfficeId = _this.selectedOfficeId
            _this.inputs.selectedStaffs = _this.selectedStaffs
          })
          .finally(() => {
            _this.isProcessing = false
          })
      })
    },
    'inputs.selectedStaffs': function (selectedStaffs) {
      let sort = 0
      this.calendar.resources = _.sortBy(selectedStaffs, ['display_name_kana', 'display_name']).map(staff => {
        sort += 1
        return { id: staff.id, title: staff.display_name, sort }
      })
      this.setSelectedStaffs({
        officeId: this.inputs.selectedOfficeId,
        staffs: this.inputs.selectedStaffs
      })
      this.loadPlans()
    },
    'users': function () {
      this.loadPlans()
    }
  },
  created: function () {
    this.exec(_this => {
      return co(function * () {
        _this.inputs.selectedOfficeId = _this.selectedOfficeId
        yield _this.initStaffShift()
        return Promise.resolve()
      })
    })
  },
  computed: {
    queryParams () {
      const staffIds = _.map(this.inputs.selectedStaffs, selectedStaff => {
        return selectedStaff.id
      })
      const userIds = _.map(this.users, user => {
        return user.id
      })
      return {
        mode: constants.QUERY_MODE_SHIFT,
        office_id: this.inputs.selectedOfficeId,
        staff_ids: staffIds,
        user_ids: userIds,
        start_day_from: this.dateRange.startDate.format('YYYYMMDD'),
        start_day_to: this.dateRange.endDate.format('YYYYMMDD')
      }
    },
    calendarApi () {
      return this.$refs.calendar.getApi()
    },
    startDate () {
      return dayjs(this.calendarApi.getDate())
    },
    endDate () {
      const currentDate = dayjs(this.calendarApi.getDate())
      return currentDate.add(DATE_RANGE, 'days')
    },
    hasTarget () {
      return this.inputs.selectedOfficeId && this.inputs.selectedStaffs.length > 0
    },
    ...mapState('staffShift', ['selectedOfficeId']),
    ...mapState('session', ['profile']),
    ...mapGetters('staffShift', ['users', 'staffs', 'selectedStaffs'])
  },
  methods: {
    datesRender ($event) {
      this.dateRange.startDate = dayjs($event.view.activeStart)
      this.dateRange.endDate = dayjs($event.view.activeEnd).add(-1, 'days')
      this.loadPlans()
    },
    onCopy (dst) {
      this.loadPlans()
    },
    mousemove (e) {
      this.tipPlace.top = (e.clientY - e.offsetY) - 70 + 'px'
      this.tipPlace.left = (e.clientX - e.offsetX) - 450 + 'px'
    },
    onPlanEditModalClosed (updated) {
      if (!updated) return
      this.loadPlans()
    },
    addSelectedStaff (selectedStaff) {
      const index = _.findIndex(this.inputs.selectedStaffs, staff => {
        return staff.id === selectedStaff.id
      })
      if (index >= 0) return
      this.inputs.selectedStaffs.push(selectedStaff)
    },
    onEventClickOuter: function ($event) {
      if (this.isProcessing) return
      const _this = this
      const prev = $event.event.extendedProps.tag
      if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: '承認が済んでいるプランです。',
          clickToClose: false,
          buttons: [
            {
              title: '閉じる',
              handler: () => {
                _this.$modal.hide('dialog')
              }
            }
          ]
        })
      } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: `報告が済んでいるプランです。編集しますか？`,
          clickToClose: false,
          buttons: [
            {
              title: 'キャンセル',
              handler: () => {
                _this.$modal.hide('dialog')
              }
            },
            {
              title: 'はい',
              handler: () => {
                _this.$modal.hide('dialog')
                _this.onEventClick($event)
              }
            }
          ]
        })
      } else {
        _this.onEventClick($event)
      }
    },
    onEventClick: function ($event) {
      this.$modal.show('plan-edit-modal', {
        params: null,
        plan: $event.event.extendedProps.tag,
        isShiftMode: true
      })
    },
    onEventResize: function ($event) {
      if (this.isProcessing) return
      const _this = this
      const prev = $event.event.extendedProps.tag
      const endHH = dayjs($event.event.end).format('HHmm')
      if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: '承認が済んでいるプランです。',
          clickToClose: false,
          buttons: [
            {
              title: '閉じる',
              handler: () => {
                $event.revert()
                _this.$modal.hide('dialog')
              }
            }
          ]
        })
      } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: `報告が済んでいるプランです。時間を変更しますか？`,
          clickToClose: false,
          buttons: [
            {
              title: 'キャンセル',
              handler: () => {
                $event.revert()
                _this.$modal.hide('dialog')
              }
            },
            {
              title: 'はい',
              handler: () => {
                _this.$modal.hide('dialog')
                const plan = convertEvent2Plan($event.event)
                _this.registerPlan(plan)
              }
            }
          ]
        })
      } else if (endHH !== prev.service.end_hhmm) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: `元の時間と異なりますが変更しますか？`,
          clickToClose: false,
          buttons: [
            {
              title: 'キャンセル',
              handler: () => {
                $event.revert()
                _this.$modal.hide('dialog')
              }
            },
            {
              title: 'はい',
              handler: () => {
                _this.$modal.hide('dialog')
                const plan = convertEvent2Plan($event.event)
                _this.registerPlan(plan)
              }
            }
          ]
        })
      }
    },
    onMouseover: function ($event) {
      this.tip.event = $event.event
      this.tip.active = true
    },
    onMouseout: function (event) {
      this.tip.active = false
    },
    onEventDropOuter: function ($event) {
      if (this.isProcessing) return

      const _this = this
      const prev = $event.event.extendedProps.tag
      const startHH = dayjs($event.event.start).format('HHmm')
      const startDay = dayjs($event.event.start).format('YYYYMMDD')
      if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: '承認が済んでいるプランです。',
          clickToClose: false,
          buttons: [
            {
              title: '閉じる',
              handler: () => {
                _this.$modal.hide('dialog')
              }
            }
          ]
        })
      } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: `報告が済んでいるプランです。時間を移動してもよろしいですか？`,
          clickToClose: false,
          buttons: [
            {
              title: 'キャンセル',
              handler: () => {
                $event.revert()
                _this.$modal.hide('dialog')
              }
            },
            {
              title: 'はい',
              handler: () => {
                _this.$modal.hide('dialog')
                _this.onEventDrop($event)
              }
            }
          ]
        })
      } else if (startDay !== prev.service.start_day || startHH !== prev.service.start_hhmm) {
        _this.$modal.show('dialog', {
          title: '確認',
          text: `元の時間と異なりますが移動しますか？`,
          clickToClose: false,
          buttons: [
            {
              title: 'キャンセル',
              handler: () => {
                $event.revert()
                _this.$modal.hide('dialog')
              }
            },
            {
              title: 'はい',
              handler: () => {
                _this.$modal.hide('dialog')
                _this.onEventDrop($event)
              }
            }
          ]
        })
      } else {
        _this.onEventDrop($event)
      }
    },
    onEventDrop: function ($event) {
      const plan = convertEvent2Plan($event.event)
      if ($event.newResource) {
        const newStaff = _.find(this.staffs, staff => {
          return staff.id === Number($event.newResource.id)
        })
        if (!newStaff) return
        const staffs = _.filter(plan.staff, staff => {
          return staff.staff_id !== Number($event.oldResource.id)
        })
        staffs.push({ staff_id: newStaff.id })
        plan.staff = staffs
      }
      this.registerPlan(plan)
    },
    showCopyWeekModal () {
      if (this.isProcessing) return
      this.$modal.show('copy-week-modal', {
        weekStart: this.dateRange.startDate
      })
    },
    copyWeek (destWeek) {
      this.exec(_this => {
        _this.isProcessing = true
        return co(function * () {
          const params = _this.queryParams
          console.info(destWeek)
          params.dest_start_day_from = destWeek.format('YYYYMMDD')
          console.info(params)
          yield api.copyShiftWeek(params)
        }).then(() => {
          _this.loadPlans()
        }).finally(() => {
          _this.isProcessing = false
        })
      })
    },
    showCopyDayModal () {
      if (this.isProcessing) return
      this.$modal.show('copy-day-modal', {
        targetDay: this.dateRange.startDate
      })
    },
    copyDay (destDay) {
      this.exec(_this => {
        _this.isProcessing = true
        return co(function * () {
          const params = _this.queryParams
          params.dest_start_day_from = destDay.format('YYYYMMDD')
          console.info(params.dest_start_day_from)
          yield api.copyShiftDay(params)
        }).then(() => {
          _this.loadPlans()
        }).finally(() => {
          _this.isProcessing = false
        })
      })
    },
    registerPlan (plan) {
      this.exec(function (_this) {
        _this.isProcessing = true
        return co(function * () {
          yield _this.savePlan(plan)
          yield _this.loadPlans()
        }).finally(() => {
          _this.isProcessing = false
        })
      })
    },
    loadPlans () {
      return this.exec(_this => {
        if (!_this.dateRange.startDate || !_this.dateRange.endDate) return
        console.info(_this.users.length)
        if (_this.inputs.userViewMode && _this.users.length < 1) {
          _this.calendar.events = []
          return
        }
        _this.isProcessing = true
        return co(function * () {
          return yield _this.getPlans(_this.queryParams)
        }).then(result => {
          _this.calendar.events = _.chain(result)
            .map(plan => {
              return convertPlan2Event(plan)
            })
            .flatten(true)
            .value()
        }).finally(() => {
          _this.isProcessing = false
        })
      })
    },
    onDateClick (event) {
      if (this.isProcessing || this.inputs.selectedStaffs.length < 1) return
      this.$modal.show('plan-edit-modal', {
        params: {
          start_day: dayjs(event.date).format('YYYY-MM-DD'),
          start_hhmm: dayjs(event.date).format('HHmm'),
          end_day: dayjs(event.date).format('YYYY-MM-DD'),
          end_hhmm: dayjs(event.date).add(30, 'minutes').format('HHmm'),
          staff_id: event.resource.id,
          isShiftMode: true
        },
        plan: null
      })
    },
    onChangeUserViewMode () {
      return this.exec(_this => {
        return co(function * () {
          yield _this.loadUsers({ officeId: _this.inputs.selectedOfficeId, userViewMode: _this.inputs.userViewMode })
          return _this.loadPlans()
        })
      })
    },
    ...mapActions('careplan', ['getPlans', 'savePlan']),
    ...mapActions('staffShift', ['initStaffShift', 'selectOffice', 'loadUsers', 'setSelectedStaffs'])
  },
  data () {
    const _this = this
    return {
      isProcessing: false,
      isWeekMode: true,
      inputs: {
        selectedOfficeId: null,
        selectedStaffs: [],
        userViewMode: false
      },
      dateRange: {
        startDate: null,
        endDate: null
      },
      tipPlace: {
        top: 0,
        left: 0
      },
      tip: {
        event: null,
        active: false
      },
      calendar: {
        locale: locale,
        scrollTime: '08:00:00',
        defaultDate: dayjs().day(1).toDate(),
        defaultView: 'resourceTimeGridWeek',
        calendarPlugins: [interactionPlugin, resourceTimeGridPlugin],
        businessHours: {},
        header: {
          left: 'prevEx,todayEx,nextEx',
          center: 'title',
          right: 'showWeek,showDay'
        },
        customButtons: {
          prevEx: {
            text: '<',
            click: function () {
              _this.calendarApi.prev()
            }
          },
          todayEx: {
            text: '今週',
            click: function () {
              _this.calendarApi.today()
            }
          },
          nextEx: {
            text: '>',
            click: function () {
              _this.calendarApi.next()
            }
          },
          copy: {
            text: 'コピー',
            click: function () {
              if (_this.isWeekMode) {
                _this.showCopyWeekModal()
              } else {
                _this.showCopyDayModal()
              }
            }
          },
          showWeek: {
            text: '週',
            click: function () {
              if (_this.isWeekMode) return
              _this.calendarApi.changeView('resourceTimeGridWeek')
              _this.isWeekMode = true
              _this.calendar.customButtons.todayEx.text = '今週'
            }
          },
          showDay: {
            text: '日',
            click: function () {
              if (!_this.isWeekMode) return
              _this.calendarApi.changeView('resourceTimeGridDay')
              _this.isWeekMode = false
              _this.calendar.customButtons.todayEx.text = '今日'
            }
          }
        },
        views: {
          resourceTimeGridWeek: {
            type: 'resourceTimeGrid',
            duration: { days: DATE_RANGE },
            buttonText: '週'
          },
          resourceTimeGridDay: {
            type: 'resourceTimeGrid',
            duration: { days: 1 },
            buttonText: '日'
          }
        },
        resources: [],
        events: []
      }
    }
  }
}
</script>

<style lang="scss">
.staff-shift-week {
  .fc-view > table {
    min-width: 100%;
    width: 6300px;
  }
}

.staff-shift-day {
  .fc-view > table {
    min-width: 100%;
    width: 900px;
  }
}

.staff-shift {
  .fc-day-header {
    border: 1px solid black !important;
  }

  .fc-day {
    border: 1px solid black !important;
  }

  .fc {
    th.fc-widget-header {
      padding: 7px 4.5px !important;
    }

    .fc-resource-cell {
      border: 1px solid black !important;
    }

    .fc-view {
      overflow-x: auto;
    }

    .fc-time-grid .fc-slats {
      z-index: 4;
      pointer-events: none;
    }

    .fc-scroller.fc-time-grid-container {
      overflow: initial !important;
    }

    .fc-scroller {
      width: 100%;
      overflow-x: hidden;
    }

  }

}

.fc-axis {
  position: sticky;
  left: 0;
  background: white;
  border-right: 1px solid black !important;
}

.tooltipevent {
  width: 250px;
  height: 100px;
  padding: 12px;
  background: #aed0ea;
  position: absolute;
  z-index: 10001;
  transform: translate3d(-50%, -100%, 0);
  font-size: 0.8rem;
  box-shadow: 1px 1px 3px 0px #888888;
  line-height: 1rem;
}
</style>
